import React, { useState } from 'react';
import { Card, Checkbox, Col, Input, Row, Space, Tag, Tooltip, Typography } from 'antd';
import type { DailyVaultOption } from '@/types';
import {
  CURRENCY_LABELS,
  DAILY_CURRENCIES,
  TAB_PREFIXES,
  TIER_LABELS,
  describeVaultOption,
  errorsAt,
  errorsUnder,
  freezeCapacity,
} from './dailyEngagementConfig';
import { useDraft } from './draftContext';
import { CardListEditor, ErrorList, LabeledNumber, NumberInput } from './fields';

const { Paragraph, Text } = Typography;

const TIER_COLORS: Record<string, string> = {
  bronze: '#ad6800',
  silver: '#8c8c8c',
  gold: '#d4b106',
  platinum: '#13a8a8',
  legend: '#722ed1',
};

/** 365-day loyalty: five tiers by Total Active Claim Days, each with a choose-1-of-3 vault. */
export const LoyaltyTab: React.FC = () => {
  const { draft, editable, errors, update } = useDraft();

  // Option ids are what the game sends when a player opens a vault: locked
  // unless the admin asks to change them (Unity builds in players' hands).
  const [idsUnlocked, setIdsUnlocked] = useState(false);
  const setOption = (t: number, o: number, change: (option: DailyVaultOption) => void) =>
    update((d) => {
      change(d.loyalty.tiers[t].options[o]);
    });

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <ErrorList prefixes={TAB_PREFIXES.loyalty} />

      <Paragraph type="secondary" style={{ marginBottom: 0 }}>
        Total Active Claim Days counts every successful claim, ever, and never resets. Reaching a
        tier earns its vault: the player picks one of three options, once. The badge only upgrades:
        lowering a threshold promotes players on their next claim; raising one never demotes anyone.
        Vault rewards are not multiplied or capped: freezes above the storage cap are kept as
        overflow (Freezes tab), never lost. A tier&apos;s freeze capacity bonus is permanent and
        adds up over the tiers reached.
      </Paragraph>

      <Space direction="vertical" size={2}>
        <Checkbox
          checked={idsUnlocked}
          disabled={!editable}
          onChange={(e) => setIdsUnlocked(e.target.checked)}
        >
          Change option ids
        </Checkbox>
        <Text type="secondary" style={{ fontSize: 12 }}>
          The game sends an option&apos;s id (A, B, C by default) when a player opens a vault.
          Renaming or swapping ids can break game builds already in players&apos; hands: change them
          only with the client team.
        </Text>
      </Space>

      {draft.loyalty.tiers.map((tier, t) => {
        const at = `loyalty.tiers[${t}]`;
        const cap = freezeCapacity(draft, t + 1);
        return (
          <Card
            key={t}
            size="small"
            title={
              <Space>
                <Tag color={TIER_COLORS[tier.tier]}>{TIER_LABELS[tier.tier] ?? tier.tier}</Tag>
                <Text type="secondary">
                  Freeze storage cap at this tier: {Number.isFinite(cap) ? cap : '?'}
                </Text>
              </Space>
            }
          >
            <Space wrap size="large" align="start" style={{ marginBottom: 12 }}>
              <LabeledNumber
                label="Threshold (claim days)"
                path={`${at}.threshold`}
                value={tier.threshold}
                min={1}
                help={t === 0 ? 'At least 1.' : 'Above the previous tier.'}
                onChange={(v) =>
                  update((d) => {
                    d.loyalty.tiers[t].threshold = v;
                  })
                }
              />
              <LabeledNumber
                label="Freeze capacity bonus"
                path={`${at}.freezeCapacityBonus`}
                value={tier.freezeCapacityBonus}
                min={0}
                addonAfter="freezes"
                help="Permanent, once reached."
                onChange={(v) =>
                  update((d) => {
                    d.loyalty.tiers[t].freezeCapacityBonus = v;
                  })
                }
              />
            </Space>

            <Row gutter={[12, 12]}>
              {tier.options.map((option, o) => {
                const ot = `${at}.options[${o}]`;
                const idErrors = errorsAt(errors, `${ot}.optionId`);
                const optionErrors = errorsUnder(errors, ot);
                return (
                  <Col key={o} xs={24} md={12} xl={8}>
                    <Card
                      size="small"
                      type="inner"
                      title={
                        <Space>
                          <Text>Option</Text>
                          <Tooltip title="Sent by the game when a player opens the vault. Change only with the client team.">
                            <Input
                              value={option.optionId}
                              disabled={!editable || !idsUnlocked}
                              status={idErrors.length > 0 ? 'error' : undefined}
                              style={{ width: 80 }}
                              onChange={(e) =>
                                setOption(t, o, (x) => {
                                  x.optionId = e.target.value;
                                })
                              }
                            />
                          </Tooltip>
                        </Space>
                      }
                    >
                      <Space direction="vertical" size={8} style={{ width: '100%' }}>
                        {DAILY_CURRENCIES.map((c) => (
                          <Space key={c} style={{ justifyContent: 'space-between', width: '100%' }}>
                            <Text>{CURRENCY_LABELS[c]}</Text>
                            <NumberInput
                              path={`${ot}.reward.${c}`}
                              value={option.reward[c]}
                              min={0}
                              onChange={(v) =>
                                setOption(t, o, (x) => {
                                  x.reward[c] = v;
                                })
                              }
                            />
                          </Space>
                        ))}
                        <Space style={{ justifyContent: 'space-between', width: '100%' }}>
                          <Text>Streak Freezes</Text>
                          <NumberInput
                            path={`${ot}.streakFreezes`}
                            value={option.streakFreezes}
                            min={0}
                            onChange={(v) =>
                              setOption(t, o, (x) => {
                                x.streakFreezes = v;
                              })
                            }
                          />
                        </Space>
                        <div>
                          <Text>Cards (up to 3)</Text>
                          <div style={{ marginTop: 4 }}>
                            <CardListEditor
                              path={`${ot}.reward.cards`}
                              value={option.reward.cards}
                              onChange={(cards) =>
                                setOption(t, o, (x) => {
                                  x.reward.cards = cards;
                                })
                              }
                            />
                          </div>
                        </div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Grants: {describeVaultOption(option)}
                        </Text>
                        {optionErrors.length > 0 && (
                          <Text type="danger" style={{ fontSize: 12 }}>
                            {optionErrors.join('; ')}
                          </Text>
                        )}
                      </Space>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          </Card>
        );
      })}
    </Space>
  );
};
